// v2 spruce — a narrow spire: six slim, slightly off-axis tiers rising from a low
// ground skirt, each spun so the stack reads shingled rather than lathe-perfect,
// in cool blue-greens that brighten toward the top, with a bare leader spike
// poking past the top tuft the way a real spruce's tip does. Variants re-nudge
// every tier, re-roll the tier radii a few percent and shift the palette
// deterministically. Ported 1:1 from age-of-mcp's v2/tree-spruce.ts, using the
// library's shared `tree` (h01, lerp, pick, tier) helpers.
import * as THREE from "three";
import { defineModel, tree, type Part } from "../../lib";

const PALETTES = [
  { skirt: "#1d4636", a: "#26563f", b: "#2e6448", lite: "#3a7656" },
  { skirt: "#1b4438", a: "#235341", b: "#2b614b", lite: "#357258" },
  { skirt: "#204833", a: "#29583c", b: "#316645", lite: "#3d7852" },
] as const;

const BARK = "#4a3624";

// [y, r, h] per tier, bottom (the ground skirt) to top.
const TIERS: readonly [number, number, number][] = [
  [0.38, 0.36, 0.52],
  [0.66, 0.3, 0.5],
  [0.94, 0.255, 0.48],
  [1.21, 0.21, 0.46],
  [1.47, 0.165, 0.44],
  [1.72, 0.12, 0.4],
];

function spruceParts(v: number): Part[] {
  const seed = 127 + v * 17;
  const pal = tree.pick(PALETTES, v);
  const parts: Part[] = [
    // a small foot of trunk, mostly hidden under the skirt
    { geometry: new THREE.CylinderGeometry(0.045, 0.07, 0.2, 5),
      color: BARK, position: [0, 0.1, 0] },
  ];
  for (let i = 0; i < TIERS.length; i++) {
    const [y, r, h] = TIERS[i]!;
    const color = i === 0 ? pal.skirt
      : i === TIERS.length - 1 ? pal.lite
      : i % 2 === 1 ? pal.a : pal.b;
    const jitter = tree.lerp(0.93, 1.07, tree.h01(seed, 5 + i));
    parts.push(tree.tier(seed, 10 + i * 3, y, r * jitter, h, color, 7, 0.035));
  }
  // the bare leader spike, poking past the top tuft
  parts.push({ geometry: new THREE.CylinderGeometry(0.012, 0.022, 0.28, 5),
    color: BARK, position: [0, 2.0, 0] });
  parts.push(tree.tier(seed, 40, 1.98, 0.07, 0.24, pal.lite, 5, 0.01));
  return parts;
}

export const treeSpruceV2 = defineModel({
  id: "tree-spruce-v2",
  batchId: "fable",
  name: "Tree spruce (v2)",
  tags: ["scenery","tree","spruce","low-poly","age-of-mcp","fable"],
  summary: "Low-poly tree spruce.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.4,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => spruceParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => spruceParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => spruceParts(2) },
  ],
  animations: [],
});
